// Copyright (c) Bentley Systems
import { SystemProperty } from "./Models/system-property";

// Display and validation rules for the IMS token settings a connector may offer. The rules key off
// property names, so they apply to any connector exposing these settings and leave every other
// property untouched. The credentials API (ConnectorTokenSettingsValidator) enforces the same rules on save.

export const DELEGATE_CALLER_TOKEN = "DelegateCallerToken";
export const GENERATE_IMS_CLIENT_TOKEN = "GenerateImsClientToken";
export const TOKEN_SCOPES = "TokenScopes";
export const IMS_CLIENT_ID = "ImsClientId";
export const IMS_CLIENT_SECRET = "ImsClientSecret";

const labels: Record<string, string> = {
  [DELEGATE_CALLER_TOKEN]: "Delegate IMS user's token",
  [GENERATE_IMS_CLIENT_TOKEN]: "Generate IMS client token",
  [TOKEN_SCOPES]: "IMS token scopes",
  [IMS_CLIENT_ID]: "IMS client ID",
  [IMS_CLIENT_SECRET]: "IMS client secret",
};

// The two token modes are mutually exclusive.
const exclusivePartners: Record<string, string> = {
  [DELEGATE_CALLER_TOKEN]: GENERATE_IMS_CLIENT_TOKEN,
  [GENERATE_IMS_CLIENT_TOKEN]: DELEGATE_CALLER_TOKEN,
};

// Dependent fields are shown only while one of their controlling flags is ticked.
const shownWhenAnySet: Record<string, string[]> = {
  [TOKEN_SCOPES]: [DELEGATE_CALLER_TOKEN, GENERATE_IMS_CLIENT_TOKEN],
  [IMS_CLIENT_ID]: [GENERATE_IMS_CLIENT_TOKEN],
  [IMS_CLIENT_SECRET]: [GENERATE_IMS_CLIENT_TOKEN],
};

// Scopes are optional, as in the credentials API.
const requiredWhenAnySet: Record<string, string[]> = {
  [IMS_CLIENT_ID]: [GENERATE_IMS_CLIENT_TOKEN],
  [IMS_CLIENT_SECRET]: [GENERATE_IMS_CLIENT_TOKEN],
};

export function getPropertyLabel(name: string): string {
  return labels[name] ?? name;
}

export function getExclusivePartner(name: string): string | undefined {
  return exclusivePartners[name];
}

export function isBooleanPropertyChecked(property: SystemProperty): boolean {
  const value: unknown = property.defaultValue;
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string" && value !== "") {
    return value.toLowerCase() === "true";
  }
  return !!property.defaultValueBool;
}

function getControllingFlags(
  properties: SystemProperty[],
  flagNames: string[] = [],
): SystemProperty[] {
  return properties.filter((p) => flagNames.includes(p.name));
}

export function isPropertyVisible(
  properties: SystemProperty[],
  name: string,
): boolean {
  const flags = getControllingFlags(properties, shownWhenAnySet[name]);
  // A connector that exposes a dependent field without its flags keeps showing it as before.
  return flags.length === 0 || flags.some(isBooleanPropertyChecked);
}

export function isPropertyRequired(
  properties: SystemProperty[],
  name: string,
): boolean {
  return getControllingFlags(properties, requiredWhenAnySet[name]).some(
    isBooleanPropertyChecked,
  );
}

export function getMissingRequiredProperties(
  properties: SystemProperty[],
): string[] {
  return properties
    .filter((p) => isPropertyRequired(properties, p.name))
    .filter((p) => {
      const value: unknown = p.defaultValue;
      return typeof value !== "string" || value.trim() === "";
    })
    .map((p) => p.name);
}

export function getVisibleProperties(
  properties: SystemProperty[],
): SystemProperty[] {
  return properties.filter((p) => isPropertyVisible(properties, p.name));
}
